import React, { useState } from 'react';
import { motion } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { Search as SearchIcon, Calendar, Download, Loader } from 'lucide-react';
import { searchInvoice, searchByDate, getAllRecords } from '../utils/api';
import { exportToExcel, getTodayDate } from '../utils/excelExport';
import RecordCard from '../components/RecordCard';

const Search = () => {
  const [searchType, setSearchType] = useState('invoice'); // 'invoice' or 'date'
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearchByInvoice = async () => {
    if (!invoiceNumber.trim()) {
      toast.error('Please enter an invoice number');
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      const response = await searchInvoice(invoiceNumber.trim());
      setRecords([response.data]);
      toast.success('Invoice found!');
    } catch (error) {
      if (error.response?.status === 404) {
        setRecords([]);
        toast.error('Invoice not found');
      } else {
        toast.error('Search failed: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchByDate = async () => {
    if (!searchDate.trim()) {
      toast.error('Please enter a date');
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      const response = await searchByDate(searchDate.trim());
      setRecords(response.data);
      
      if (response.count === 0) {
        toast('No records found for this date', { icon: 'ℹ️' });
      } else {
        toast.success(`Found ${response.count} record(s)`);
      }
    } catch (error) {
      toast.error('Search failed: ' + (error.response?.data?.error || error.message));
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchType === 'invoice') {
      handleSearchByInvoice();
    } else {
      handleSearchByDate();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleExportToExcel = () => {
    if (records.length === 0) {
      toast.error('No records to export');
      return;
    }

    try {
      const filename = searchType === 'invoice' 
        ? `invoice_${invoiceNumber}` 
        : `invoices_${searchDate}`;
      
      exportToExcel(records, filename);
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      toast.error('Export failed: ' + error.message);
    }
  };

  const setTodayDate = () => {
    setSearchDate(getTodayDate());
  };

  return (
    <div className="search-page">
      <Toaster position="top-right" />

      <motion.div
        className="search-container"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="search-header">
          <h1 className="search-title">Search Records</h1>
          <p className="search-subtitle">Find invoices by invoice number or date</p>
        </div>

        <div className="search-controls">
          <div className="search-type-tabs">
            <button
              className={`tab ${searchType === 'invoice' ? 'active' : ''}`}
              onClick={() => setSearchType('invoice')}
            >
              <SearchIcon size={20} />
              <span>By Invoice Number</span>
            </button>
            <button
              className={`tab ${searchType === 'date' ? 'active' : ''}`}
              onClick={() => setSearchType('date')}
            >
              <Calendar size={20} />
              <span>By Date</span>
            </button>
          </div>

          <div className="search-input-section">
            {searchType === 'invoice' ? (
              <div className="input-wrapper">
                <SearchIcon className="input-icon" size={20} />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Enter invoice number (e.g., INV-2024-001)"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
              </div>
            ) : (
              <div className="input-wrapper">
                <Calendar className="input-icon" size={20} />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Enter date (DD-MM-YYYY)"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button className="today-button" onClick={setTodayDate}>
                  Today
                </button>
              </div>
            )}

            <button
              className="search-button"
              onClick={handleSearch}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader className="spin" size={20} />
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <SearchIcon size={20} />
                  <span>Search</span>
                </>
              )}
            </button>
          </div>

          {records.length > 0 && (
            <motion.button
              className="export-button"
              onClick={handleExportToExcel}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Download size={20} />
              <span>Export to Excel ({records.length} record{records.length !== 1 ? 's' : ''})</span>
            </motion.button>
          )}
        </div>

        <div className="results-section">
          {isLoading ? (
            <div className="loading-state">
              <Loader className="spinner" size={48} />
              <p>Searching blockchain...</p>
            </div>
          ) : hasSearched ? (
            records.length > 0 ? (
              <motion.div
                className="results-grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {records.map((record, index) => (
                  <RecordCard key={record.id || index} record={record} index={index} />
                ))}
              </motion.div>
            ) : (
              <div className="empty-state">
                <SearchIcon size={64} className="empty-icon" />
                <h3>No Records Found</h3>
                <p>Try searching with a different invoice number or date</p>
              </div>
            )
          ) : (
            <div className="empty-state">
              <SearchIcon size={64} className="empty-icon" />
              <h3>Start Searching</h3>
              <p>Enter an invoice number or date to search records</p>
            </div>
          )}
        </div>
      </motion.div>

      <style jsx="true">{`
        .search-page {
          min-height: calc(100vh - 80px);
          padding: 40px 24px;
        }

        .search-container {
          max-width: 1400px;
          margin: 0 auto;
        }

        .search-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .search-title {
          font-size: 36px;
          font-weight: 800;
          color: white;
          margin-bottom: 12px;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
        }

        .search-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.9);
        }

        .search-controls {
          background: white;
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          margin-bottom: 32px;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .search-type-tabs {
          display: flex;
          gap: 12px;
          padding: 6px;
          background: #f1f5f9;
          border-radius: 12px;
        }

        .tab {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 24px;
          border: none;
          background: transparent;
          color: #64748b;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .tab:hover {
          background: white;
          color: #667eea;
        }

        .tab.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .search-input-section {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .input-wrapper {
          flex: 1;
          position: relative;
          display: flex;
          align-items: center;
          min-width: 300px;
        }

        .input-icon {
          position: absolute;
          left: 16px;
          color: #94a3b8;
        }

        .search-input {
          width: 100%;
          padding: 14px 16px 14px 48px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .today-button {
          position: absolute;
          right: 8px;
          padding: 6px 12px;
          background: #f1f5f9;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          color: #667eea;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .today-button:hover {
          background: #e2e8f0;
        }

        .search-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .search-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
        }

        .search-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        .export-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 24px;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .export-button:hover {
          background: #059669;
          box-shadow: 0 8px 16px rgba(16, 185, 129, 0.3);
        }

        .results-section {
          min-height: 400px;
        }

        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 24px;
        }

        .loading-state,
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 40px;
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .spinner {
          color: #667eea;
          animation: spin 1s linear infinite;
        }

        .empty-icon {
          color: #cbd5e1;
          margin-bottom: 24px;
        }

        .loading-state p,
        .empty-state p {
          color: #64748b;
          font-size: 16px;
          margin-top: 16px;
        }

        .empty-state h3 {
          font-size: 24px;
          color: #1e293b;
          margin-bottom: 8px;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .search-controls {
            padding: 24px;
          }

          .search-type-tabs {
            flex-direction: column;
          }

          .search-input-section {
            flex-direction: column;
          }

          .input-wrapper {
            min-width: 100%;
          }

          .search-button,
          .export-button {
            width: 100%;
          }

          .results-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Search;
